import React, {useReducer, useState} from "react";
import PropTypes from "prop-types";
import ResultNoticeService from "services/ResultNoticeService";
import {makeStyles, TableCell, TableRow} from "@material-ui/core";
import iconWorksReducer from "libs/iconWorksReducer";
import DateFormats from "constants/DateFormats";
import moment from "libs/moment";
import WorkableIconPreviewButton from "components/button/WorkableIconPreviewButton";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import Tr from "translations/Translations";
import WorkableIconEditButton from "components/button/WorkableIconEditButton";
import RouterPath from "libs/RouterPath";
import WorkableIconUnpublishButton from "components/button/WorkableIconUnpublishButton";
import WorkableIconPublishButton from "components/button/WorkableIconPublishButton";
import WorkableIconShareButton from "components/button/WorkableIconShareButton";
import NationalNoticeService from "../../../../../../../../services/NationalNoticeService";
import {MO_REQUIRED_GROUPS} from "../../../../../../../../constants/EzamowieniaRequiredGroups";
import useEzamowieniaOauth from "../../../../../../../../hooks/useEzamowieniaOauth";
import WorkableIconCheckButton from "../../../../../../../../components/button/WorkableIconCheckButton";
import WorkableIconDownloadButton from "components/button/WorkableIconDownloadButton";
import BzpNoticeDialog from "pages/purchaser/MyProcedure/scenes/part1/panels/nationalNotice/BzpNoticeDialog";
import useSnackbar from "../../../../../../../../hooks/useSnackbar";

const useStyles = makeStyles(theme => ({
    buttonColumn: theme.components.table.buttonColumn,
    descriptionTooltip: {
        whiteSpace: 'pre-line'
    }
}))

function BzpResultNoticeRow(
    {
        demandId,
        editable,
        updateExists,
        resultNotice,
        bzpResultNoticeSend,
        bzpResultNoticePublishedOnEzamowienia,
        attachment,
        disabled
    }) {
    const classes = useStyles();
    const [dialogOpen, setDialogOpen] = useState(false);
    const {authorizeEzamowienia} = useEzamowieniaOauth();
    const {enqueueSuccessSnackbar} = useSnackbar();

    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        preview: false,
        publish: false,
        sendToEZamowienia: false,
        checkNoticeStatus: false,
        delete: false
    });

    const deleteResultNotice = (file) => {
        dispatchIconWorks({type: 'delete', works: true})
        ResultNoticeService.deleteResultNotice(demandId, resultNotice.identity, file.identity).finally(() => dispatchIconWorks({
            type: 'delete',
            works: false
        }));
    };

    const goToNationalTenderResultNotice = () => {
        const href = RouterPath.createPath(RouterPath.myProcedure.steps.MP_NATIONAL_NOTICE_FORM_TYPE_ID, {
            formType: "resultNotice",
            id: demandId
        });
        RouterPath.redirect(href);
    }

    const sendContractNotice = () => {
        dispatchIconWorks({type: 'sendToEZamowienia', works: true})

        if (resultNotice.update) {
            authorizeEzamowienia(() => {
                NationalNoticeService.publishTenderResultNoticeUpdate(demandId)
                    .then((success) => {
                        if (success) {
                            enqueueSuccessSnackbar(Tr("bzp_result_notice_update_sent"));
                        }
                    })
                    .catch((e) => console.error(e))
                    .finally(() => {
                        dispatchIconWorks({type: 'sendToEZamowienia', works: false});
                    })
            }, () => {
                dispatchIconWorks({type: 'sendToEZamowienia', works: false});
            }, MO_REQUIRED_GROUPS)
        } else {
            authorizeEzamowienia(() => {
                NationalNoticeService.publishResultNotice(demandId)
                    .then((success) => {
                        if (success) {
                            enqueueSuccessSnackbar(Tr("bzp_tender_result_notice_sent"));
                        }
                    })
                    .catch((e) => console.error(e))
                    .finally(() => {
                        dispatchIconWorks({type: 'sendToEZamowienia', works: false});
                    })
            }, () => {
                dispatchIconWorks({type: 'sendToEZamowienia', works: false});
            }, MO_REQUIRED_GROUPS)
        }
    }

    const checkTenderResultNoticeStatus = () => {
        dispatchIconWorks({type: 'checkNoticeStatus', works: true});
        authorizeEzamowienia(() => {
            NationalNoticeService.checkTenderResultNoticeStatus(demandId).catch((e) => console.error(e)).finally(() => dispatchIconWorks({
                type: 'checkNoticeStatus',
                works: false
            }))
        }, () => {
            dispatchIconWorks({type: 'checkNoticeStatus', works: false});
        }, MO_REQUIRED_GROUPS)
    }

    const noticePublication = (file, publish) => {
        dispatchIconWorks({type: 'publish', works: true})
        const func = publish ? ResultNoticeService.publishResultNotice : ResultNoticeService.unpublishResultNotice;
        func(demandId, resultNotice.identity).finally(() => dispatchIconWorks({type: 'publish', works: false}));
    };

return (
    <>
        <TableRow key={attachment.identity}>
            <TableCell>{resultNotice.update ? Tr("bzp_result_notice_update") : Tr("bzp_tender_result_notice")}</TableCell>
            <TableCell>{moment(attachment.createDate).format(DateFormats.DATETIME_WITH_SECONDS_FORMAT)}</TableCell>
            <TableCell>{attachment.publishDate && moment(attachment.publishDate).format(DateFormats.DATETIME_WITH_SECONDS_FORMAT)}</TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconDownloadButton 
                onClick={() => setDialogOpen(true)}
                disabled={!attachment.publishDate}
                />
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconPreviewButton
                    onClick={() => goToNationalTenderResultNotice()}
                    disabled={disabled}
                />
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconEditButton
                    onClick={goToNationalTenderResultNotice}
                    disabled={disabled || attachment?.publishDate != null}
                />
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconDeleteButton
                    working={iconWorks.delete}
                    onClick={() => deleteResultNotice(attachment)}
                    disabled={resultNotice.published || attachment.publishDate != null || bzpResultNoticeSend}
                />
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                {
                    resultNotice.published ?
                        <WorkableIconUnpublishButton
                            working={iconWorks.publish} disabled={!editable || iconWorks.anyWorks || !bzpResultNoticeSend}
                            onClick={() => noticePublication(attachment, false)}
                        /> :
                        <WorkableIconPublishButton
                            working={iconWorks.publish} disabled={!editable || iconWorks.anyWorks || !bzpResultNoticeSend}
                            onClick={() => noticePublication(attachment, true)}
                        />
                }
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconShareButton
                    tooltip={attachment.publishDate != null ? Tr('bzp_notice_published') : Tr("bzp_notice_publish")}
                    working={iconWorks.sendToEZamowienia}
                    onClick={() => sendContractNotice()}
                    disabled={!resultNotice.complete || attachment.publishDate != null || bzpResultNoticeSend}
                />
            </TableCell>
            <TableCell className={classes.buttonColumn}>
                <WorkableIconCheckButton
                    onClick={checkTenderResultNoticeStatus}
                    disabled={!bzpResultNoticeSend || attachment.publishDate != null || bzpResultNoticePublishedOnEzamowienia}
                    working={iconWorks.checkNoticeStatus}
                    tooltip={Tr('bzp_notice_status')}>
                </WorkableIconCheckButton>
            </TableCell>
        </TableRow>
        {!!attachment.publishDate && <BzpNoticeDialog
            id={demandId}
            resultNoticeId={resultNotice.identity}
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
        />
        } 
    </>
    )
}

BzpResultNoticeRow.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    editable: PropTypes.bool.isRequired,
    updateExists: PropTypes.bool.isRequired,
    resultNotice: PropTypes.object.isRequired,
    bzpResultNoticeSend: PropTypes.bool.isRequired,
    bzpResultNoticePublishedOnEzamowienia: PropTypes.bool.isRequired,
}


export default BzpResultNoticeRow;